import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Spinner, Modal, Button, Form } from 'react-bootstrap';
import { isDeveloperOrAdmin } from '../utils/permissions';
import { showToast as toast } from '../toastManager.js';
import errorMessages from '../errorMessages.js';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useQueue } from '../context/QueueContext';

function ExistingDivs({ ViewedPage, shop_domain, app_url }) {
    ViewedPage(25);

    const [loading, setLoading] = useState(false);
    const [selectedSectionType, setSelectedSectionType] = useState('all');
    const [allSections, setAllSections] = useState([]);
    const [filteredSections, setFilteredSections] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const { addJob, addInstantJob } = useQueue();

    // Tag & Approve (combined) modal states
    const [showTagAndApproveModal, setShowTagAndApproveModal] = useState(false);
    const [tagAndApproveBusy, setTagAndApproveBusy] = useState(false);
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [selectedSectionName, setSelectedSectionName] = useState('');
    const [landingPages, setLandingPages] = useState([]);
    const [selectedLandingPage, setSelectedLandingPage] = useState('');
    const [sectionsTags, setSectionsTags] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubCategory, setSelectedSubCategory] = useState('');

    // Delete modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState('');
    const [pendingDeleteName, setPendingDeleteName] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Info modal state
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoSection, setInfoSection] = useState(null);
    const [experimentLandingPages, setExperimentLandingPages] = useState([]);
    const [sectionTagsMap, setSectionTagsMap] = useState({});

    // LP Preview picker modal state — user picks which tagged LP to preview on
    const [showLpPickerModal, setShowLpPickerModal] = useState(false);
    const [lpPickerSection, setLpPickerSection] = useState(null);
    const [lpPickerSelected, setLpPickerSelected] = useState('');
    const [lpPickerOptions, setLpPickerOptions] = useState([]);
    const [lpPickerBusy, setLpPickerBusy] = useState(false);

    const isMountedRef = useRef(true);
    const htmlCacheRef = useRef({});   // { section_id: htmlContent } — persists across filter changes
    const observerRef  = useRef(null); // active IntersectionObserver

    // ── AI Enhance ─────────────────────────────────────────────────────────────
    // Hands the section off to the AI editor (AIAssetContentGenerator) by
    // pre-writing its sessionStorage seed, then navigates. The page reads this
    // key on first render and lands with the section preselected in
    // "Improve Existing" mode.
    const handleEnhanceClick = async (section) => {
        if (!section?.section_id) {
            toast.error('Missing section id — cannot open AI editor');
            return;
        }

        const SESSION_KEY = `sxi_ai_gen_${shop_domain || 'default'}`;
        let existingSession = {};
        try { existingSession = JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; }
        catch { existingSession = {}; }

        const sectionAsset = {
            _type: 'section',
            section_id:             section.section_id,
            section_name:           section.section_name || section.section_id,
            url:                    section.url || '',
            section_type:           section.section_type || 'imported',
            section_query_selector: section.section_query_selector || '',
            parent_query_selector:  section.parent_query_selector  || '',
            section_original_url:   section.section_original_url   || '',
        };

        const handoff = {
            ...existingSession,            // preserve llmProvider / llmModel / selectedPage if previously set
            selectedAssets:     [sectionAsset],
            mode:               'improve',
            outputType:         'sections',
            isConfigLocked:     false,     // user still needs to confirm page
            isConfigOpen:       true,      // land directly on the config drawer
            activeView:         'chat',
            // Reset chat so a stale prior conversation about a different section doesn't leak in
            messages:           [],
            chatSummary:        '',
            lastExchange:       null,
            generatedSections:  [],
            sessionCacheId:     null,
        };

        try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(handoff));
        } catch (e) {
            console.error('Failed to write AI editor handoff:', e);
            toast.error('Could not open AI editor — sessionStorage unavailable');
            return;
        }

        window.location.href = '/ai-asset-content-generator';
    };

    // ── Helpers ─────────────────────────────────────────────────────────────────
    const filterSectionsByType = (sections, sectionType) => {
        if (sectionType === 'all') return sections;
        return sections.filter(s => s.section_type === sectionType);
    };

    const fetchLandingPages = async () => {
        try {
            const response = await axios.post(app_url + "/api/firebase/get_landing_pages", {}, { withCredentials: true });
            if (response.data.success) setLandingPages(response.data.landing_pages || []);
            else toast.error('Failed to load landing pages');
        } catch (error) {
            console.error('Error fetching landing pages:', error);
            toast.error('Failed to load landing pages');
        }
    };

    const fetchExperimentLandingPages = async () => {
        try {
            const res = await axios.get(`${app_url}/api/dev/landing-page-urls/${shop_domain}`, { withCredentials: true });
            if (res.data.success) setExperimentLandingPages(res.data.data || []);
        } catch (e) { console.error('Error fetching experiment landing pages:', e); }
    };

    // ── Info modal handler ──────────────────────────────────────────────────────
    const handleInfoClick = (result) => {
        setInfoSection(result);
        setShowInfoModal(true);
    };

    // ── Tag & Approve (combined) handlers ───────────────────────────────────────
    // One LP serves as BOTH the tag target and the approve target. Submit fires
    // PUT (tag) → POST (approve) sequentially with that single value.
    const handleTagAndApproveClick = (section) => {
        setSelectedSectionId(section.section_id);
        setSelectedSectionName(section.section_name || section.section_id);
        setSelectedLandingPage('');
        setSelectedCategory('');
        setSelectedSubCategory('');
        setShowTagAndApproveModal(true);
    };

    const handleTagAndApproveSubmit = async () => {
        if (!selectedLandingPage) { toast.error('Pick a landing page'); return; }
        setTagAndApproveBusy(true);
        try {
            // 1) Tag — the chosen LP becomes the section's tagged_landing_pages list.
            const newTags = [selectedLandingPage];
            await axios.put(
                `${app_url}/api/dev/imported-sections/${shop_domain}/${selectedSectionId}`,
                { tagged_landing_pages: newTags },
                { withCredentials: true }
            );
            setSectionTagsMap(prev => ({ ...prev, [selectedSectionId]: newTags }));
            // Triggers renderSections via the [allSections] useEffect — rebinds the LP Preview button's stale DOM closure.
            setAllSections(prev => prev.map(s =>
                s.section_id === selectedSectionId
                    ? { ...s, tagged_landing_pages: newTags }
                    : s
            ));

            // 2) Approve — same LP.
            const response = await axios.post(`${app_url}/api/firebase/save_section`, {
                section_ids: [selectedSectionId],
                landing_page: selectedLandingPage,
                ...(selectedCategory && {
                    section_metadata: {
                        [selectedSectionId]: { section_category: selectedCategory, section_sub_category: selectedSubCategory },
                    },
                }),
            }, { withCredentials: true });

            if (response.status === 202 && response.data?.jobId) {
                addJob(response.data.jobId, selectedSectionId, selectedSectionName, selectedLandingPage, app_url);
            } else if (response.data?.success) {
                toast.success(`Section tagged and approved to ${selectedLandingPage}`);
            } else {
                toast.error(response.data?.message || 'Approve failed');
                return;
            }
            setShowTagAndApproveModal(false);
        } catch (err) {
            console.error('Tag & Approve error:', err);
            toast.error('Failed to tag or approve section');
        } finally {
            setTagAndApproveBusy(false);
        }
    };

    // ── LP Preview ──────────────────────────────────────────────────────────────
    // Click handler opens a small picker so the user can choose which of the
    // section's tagged landing pages to preview the section on. We never use
    // section_original_url here — only the explicitly tagged LPs are eligible.
    const handleLpPreviewClick = (section) => {
        if (!section?.section_id) { toast.error('Missing section id'); return; }
        if (!section.url) { toast.error('Section has no live file_url'); return; }
        const taggedLps = sectionTagsMap[section.section_id] ?? section.tagged_landing_pages ?? [];
        if (!Array.isArray(taggedLps) || taggedLps.length === 0) {
            toast.error('Section is not tagged to any landing page. Use "Tag & Approve" first.');
            return;
        }
        setLpPickerSection(section);
        setLpPickerOptions([...taggedLps]);
        setLpPickerSelected(taggedLps[0]);
        setShowLpPickerModal(true);
    };

    // Confirms the picker and runs the actual preview generation against the
    // chosen LP via /api/cux/generate-preview (Worker pipeline). Only the
    // clicked section is injected into the LP — no other tagged sections.
    const submitLpPreview = async () => {
        const section = lpPickerSection;
        const lpUrl = lpPickerSelected;
        if (!section || !lpUrl) return;

        setLpPickerBusy(true);
        try {
            // Pull the LP's canonical rearrangement (real selectors + categories
            // straight from the section_json step). Without this we'd ship only
            // the one imported section and the rest of the LP would not render.
            let lpRearrangement = [];
            try {
                const r = await axios.get(`${app_url}/api/ai_generation/sections_rearrangement`,
                    { params: { page: lpUrl }, withCredentials: true });
                if (r.data?.success && Array.isArray(r.data.data)) lpRearrangement = r.data.data;
            } catch (e) {
                console.warn('Could not fetch LP sections_rearrangement:', e?.message);
            }

            // Imported sections are injected as new nodes — query selector and
            // parent are invariant. Default rearrangement type is 'dynamic'
            // (matches dev_controller section_json normalisation).
            const importedEntry = {
                section_id:             section.section_id,
                section_name:           section.section_name || section.section_id,
                section_query_selector: 'sxi_section',
                parent_query_selector:  '',
                section_rearrangement:  'dynamic',
                section_category:       section.section_category     || '',
                section_sub_category:   section.section_sub_category || '',
                display_rules:          {},
            };

            const merged = [...lpRearrangement];
            const idx = merged.findIndex(s => s.section_id === importedEntry.section_id);
            if (idx >= 0) merged[idx] = importedEntry; else merged.push(importedEntry);

            // Stable key per (section, lp) so re-clicks upsert the same D1 entry.
            const key = `section_preview__${section.section_id}`;
            const value = {
                template_id: '',
                customization: [],
                rearrangement: merged,
                rearrangement_mobile: [],
            };

            const res = await axios.post(`${app_url}/api/cux/generate-preview`,
                { landing_page_url: lpUrl, key, value },
                { withCredentials: true }
            );
            if (!res.data?.success) {
                throw new Error(res.data?.message || 'Preview generation failed');
            }

            const previewUrl = `${lpUrl}${lpUrl.includes('?') ? '&' : '?'}sxi_content=${encodeURIComponent(key)}&_cb=${Date.now()}`;
            window.open(previewUrl, '_blank', 'noopener,noreferrer');
            toast.success('LP preview opened. Tip: paste into incognito for a fully clean session.');
            setShowLpPickerModal(false);
        } catch (err) {
            console.error('LP preview error:', err);
            toast.error(err.message || 'LP preview failed');
        } finally {
            setLpPickerBusy(false);
        }
    };

    // ── Delete handlers ──────────────────────────────────────────────────────────
    const handleDeleteClick = (sectionId, sectionName) => {
        setPendingDeleteId(sectionId);
        setPendingDeleteName(sectionName || sectionId);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            const response = await axios.post(app_url + "/api/firebase/delete_imported_section", { section_ids: [pendingDeleteId] }, { withCredentials: true });
            if (response.data.success) {
                addInstantJob(pendingDeleteId, pendingDeleteName, 'delete', 'succeeded', ['Section deleted successfully']);
                const sanitizedId = pendingDeleteId.replace(/[^a-zA-Z0-9_-]/g, '_');
                document.getElementById(`wrapper_${sanitizedId}`)?.remove();
                delete htmlCacheRef.current[pendingDeleteId];
            }
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.conflicts) {
                addInstantJob(pendingDeleteId, pendingDeleteName, 'delete', 'failed', ['Cannot delete — section is currently in use'], error.response.data.conflicts);
            } else {
                toast.error('Failed to delete section');
            }
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setPendingDeleteId('');
            setPendingDeleteName('');
        }
    };

    // ── Lazy loading core ────────────────────────────────────────────────────────

    // Fetch HTML for one section and inject it; reads/writes htmlCacheRef
    const loadIframeContent = async (result, iframe, placeholder) => {
        let html = htmlCacheRef.current[result.section_id];
        if (!html) {
            try {
                const response = await axios.post(app_url + "/api/firebase/get_section_html", { url: result.url }, { withCredentials: true });
                html = response.data.data;
                htmlCacheRef.current[result.section_id] = html;
            } catch (error) {
                if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
                console.error(`Error fetching HTML for ${result.section_id}:`, error);
                html = `<div style="padding:12px;color:#721c24;">Error loading preview</div>`;
            }
        }
        if (!isMountedRef.current || !iframe.isConnected) return;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open(); iframeDoc.write(html); iframeDoc.close();
        // Assign a unique id to the iframe's <html> element and scope the no-scrollbar
        // rule to it — keeps inner scrollable elements and the outer page unaffected
        const rootId = `sxi-root-${iframe.id}`;
        iframeDoc.documentElement.id = rootId;
        const noScrollStyle = iframeDoc.createElement('style');
        noScrollStyle.textContent = `#${rootId}::-webkit-scrollbar{display:none}#${rootId}{-ms-overflow-style:none;scrollbar-width:none;}`;
        (iframeDoc.head || iframeDoc.documentElement).appendChild(noScrollStyle);
        placeholder.style.display = 'none';
        iframe.style.display = 'block';
    };

    // Build a full card DOM shell (no network calls); returns the wrapperDiv
    const buildSectionShell = (result) => {
        const sanitizedId = result.section_id.replace(/[^a-zA-Z0-9_-]/g, '_');

        // ── Wrapper ──
        const wrapperDiv = document.createElement("div");
        wrapperDiv.id = `wrapper_${sanitizedId}`;
        wrapperDiv.className = "sxi-existing-div-wrapper";
        wrapperDiv.style.cssText = "position:relative;cursor:default;margin-bottom:1rem;border:1px solid #e9ecef;border-radius:8px;transition:all 0.3s ease;padding:10px;background-color:#ffffff;pointer-events:none;z-index:0;";
        wrapperDiv.addEventListener('mouseenter', () => { wrapperDiv.style.border = "1px solid #007bff"; wrapperDiv.style.backgroundColor = "#f8f9fa"; });
        wrapperDiv.addEventListener('mouseleave', () => { wrapperDiv.style.border = "1px solid #e9ecef"; wrapperDiv.style.backgroundColor = "#ffffff"; });

        // ── Floating info icon (top-right overlay) ──
        const infoIconBtn = document.createElement("button");
        infoIconBtn.title = "Section info";
        infoIconBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8" stroke-width="3"/><line x1="12" y1="12" x2="12" y2="17"/></svg>`;
        infoIconBtn.style.cssText = "position:absolute;top:10px;right:10px;z-index:10;display:flex;align-items:center;justify-content:center;width:26px;height:26px;background:rgba(255,255,255,0.9);border:1px solid #ced4da;border-radius:50%;cursor:pointer;color:#6c757d;padding:0;pointer-events:auto;backdrop-filter:blur(2px);";
        infoIconBtn.addEventListener('mouseenter', () => { infoIconBtn.style.backgroundColor = '#e9ecef'; });
        infoIconBtn.addEventListener('mouseleave', () => { infoIconBtn.style.backgroundColor = 'rgba(255,255,255,0.9)'; });
        infoIconBtn.addEventListener('click', (e) => { e.stopPropagation(); handleInfoClick(result); });

        // ── Loading placeholder ──
        const placeholder = document.createElement("div");
        placeholder.id = `placeholder_${sanitizedId}`;
        placeholder.style.cssText = "height:30rem;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f8f9fa;border-radius:4px;gap:8px;";
        placeholder.innerHTML = `<div class="spinner-border spinner-border-sm text-secondary" role="status"></div><span style="font-size:13px;color:#6c757d;">Loading preview...</span>`;

        // ── Iframe (hidden until content loads) ──
        const iframe = document.createElement("iframe");
        iframe.id = sanitizedId;
        iframe.className = "sxi-existing-div-preview";
        iframe.style.cssText = "display:none;width:100%;height:30rem;pointer-events:auto;touch-action:auto;position:relative;z-index:1;";

        // ── Button bar ──
        const btnContainer = document.createElement("div");
        btnContainer.style.cssText = "display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:10px;width:100%;pointer-events:auto;";
        const leftBtnGroup  = document.createElement("div"); leftBtnGroup.style.cssText  = "display:flex;gap:8px;";
        const rightBtnGroup = document.createElement("div"); rightBtnGroup.style.cssText = "display:flex;gap:8px;";

        // Edit
        const editButton = document.createElement("button");
        editButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px;"><path d="M7 7H6C5.46957 7 4.96086 7.21071 4.58579 7.58579C4.21071 7.96086 4 8.46957 4 9V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H15C15.5304 20 16.0391 19.7893 16.4142 19.4142C16.7893 19.0391 17 18.5304 17 18V17" stroke="#132848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 5.00011L19 8.00011M20.385 6.58511C20.7788 6.19126 21.0001 5.65709 21.0001 5.10011C21.0001 4.54312 20.7788 4.00895 20.385 3.61511C19.9912 3.22126 19.457 3 18.9 3C18.343 3 17.8088 3.22126 17.415 3.61511L9 12.0001V15.0001H12L20.385 6.58511Z" stroke="#132848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Edit`;
        editButton.style.cssText = "display:flex;align-items:center;font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#212529;border:1px solid #dee2e6;cursor:pointer;background-color:#ffffff;white-space:nowrap;transition:all 0.2s ease;";
        editButton.addEventListener('mouseenter', () => { editButton.style.backgroundColor = "#f8f9fa"; });
        editButton.addEventListener('mouseleave', () => { editButton.style.backgroundColor = "#ffffff"; });
        editButton.addEventListener('click', async (event) => {
            event.stopPropagation();
            // Ensure HTML is loaded before opening editor
            let html = htmlCacheRef.current[result.section_id];
            if (!html) {
                editButton.textContent = 'Loading...';
                editButton.disabled = true;
                try {
                    const r = await axios.post(app_url + "/api/firebase/get_section_html", { url: result.url }, { withCredentials: true });
                    html = r.data.data;
                    htmlCacheRef.current[result.section_id] = html;
                } catch (e) {
                    toast.error('Failed to load section content');
                    editButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px;"><path d="M7 7H6C5.46957 7 4.96086 7.21071 4.58579 7.58579C4.21071 7.96086 4 8.46957 4 9V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H15C15.5304 20 16.0391 19.7893 16.4142 19.4142C16.7893 19.0391 17 18.5304 17 18V17" stroke="#132848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 5.00011L19 8.00011M20.385 6.58511C20.7788 6.19126 21.0001 5.65709 21.0001 5.10011C21.0001 4.54312 20.7788 4.00895 20.385 3.61511C19.9912 3.22126 19.457 3 18.9 3C18.343 3 17.8088 3.22126 17.415 3.61511L9 12.0001V15.0001H12L20.385 6.58511Z" stroke="#132848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Edit`;
                    editButton.disabled = false;
                    return;
                }
            }
            sessionStorage.setItem('current_edit_div', html);
            sessionStorage.setItem('current_section_id', result.section_id);
            sessionStorage.setItem('current_section_name', result.section_name);
            sessionStorage.setItem('edit_source', result.section_type || 'imported');
            sessionStorage.setItem('section_type', result.section_type || 'imported');
            window.location.href = '/editor';
        });

        // Preview
        const previewButton = document.createElement("button");
        previewButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="margin-right: 6px;"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>Preview`;
        previewButton.style.cssText = "display:flex;align-items:center;font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#212529;border:1px solid #dee2e6;cursor:pointer;background-color:#ffffff;white-space:nowrap;transition:all 0.2s ease;";
        previewButton.addEventListener('mouseenter', () => { previewButton.style.backgroundColor = "#f8f9fa"; });
        previewButton.addEventListener('mouseleave', () => { previewButton.style.backgroundColor = "#ffffff"; });
        previewButton.addEventListener('click', (event) => {
            event.stopPropagation();
            const sectionUrl = result.section_url || result.url;
            if (sectionUrl) window.open(sectionUrl, '_blank', 'noopener,noreferrer');
            else toast.warn('Preview URL unavailable for this section');
        });

        // Delete
        const deleteButton = document.createElement("button");
        deleteButton.textContent = "Delete";
        deleteButton.style.cssText = "font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#212529;border:1px solid #dee2e6;cursor:pointer;background-color:#ffffff;white-space:nowrap;transition:all 0.2s ease;";
        deleteButton.addEventListener('mouseenter', () => { deleteButton.style.backgroundColor = "#f8f9fa"; });
        deleteButton.addEventListener('mouseleave', () => { deleteButton.style.backgroundColor = "#ffffff"; });
        deleteButton.addEventListener('click', (event) => { event.stopPropagation(); handleDeleteClick(result.section_id, result.section_name); });

        // Tag & Approve (merged — replaces the old Tag + Approve Sections buttons)
        const tagAndApproveButton = document.createElement("button");
        tagAndApproveButton.textContent = "Approve";
        tagAndApproveButton.style.cssText = "font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#ffffff;border:none;cursor:pointer;background-color:#132848;white-space:nowrap;transition:all 0.2s ease;";
        tagAndApproveButton.addEventListener('mouseenter', () => { tagAndApproveButton.style.backgroundColor = "#0f1f35"; });
        tagAndApproveButton.addEventListener('mouseleave', () => { tagAndApproveButton.style.backgroundColor = "#132848"; });
        tagAndApproveButton.addEventListener('click', (event) => { event.stopPropagation(); handleTagAndApproveClick(result); });

        // LP Preview — available to everyone. Opens a picker so the user can
        // choose which of the section's tagged LPs to preview the section on.
        const lpPreviewButton = document.createElement("button");
        lpPreviewButton.textContent = "LP Preview";
        lpPreviewButton.style.cssText = "font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#212529;border:1px solid #dee2e6;cursor:pointer;background-color:#ffffff;white-space:nowrap;transition:all 0.2s ease;";
        lpPreviewButton.addEventListener('mouseenter', () => { if (!lpPreviewButton.disabled) lpPreviewButton.style.backgroundColor = "#f8f9fa"; });
        lpPreviewButton.addEventListener('mouseleave', () => { if (!lpPreviewButton.disabled) lpPreviewButton.style.backgroundColor = "#ffffff"; });
        lpPreviewButton.addEventListener('click', (event) => { event.stopPropagation(); handleLpPreviewClick(result); });

        // AI Enhance — admin/developer only.
        let enhanceButton = null;
        if (isDeveloperOrAdmin()) {
            enhanceButton = document.createElement("button");
            enhanceButton.textContent = "✦ AI Enhance";
            enhanceButton.style.cssText = "font-size:14px;padding:8px 16px;border-radius:4px;font-weight:500;color:#7c3aed;border:1px solid #7c3aed;cursor:pointer;background-color:#ffffff;white-space:nowrap;transition:all 0.2s ease;";
            enhanceButton.addEventListener('mouseenter', () => { if (!enhanceButton.disabled) enhanceButton.style.backgroundColor = "#f5f3ff"; });
            enhanceButton.addEventListener('mouseleave', () => { if (!enhanceButton.disabled) enhanceButton.style.backgroundColor = "#ffffff"; });
            enhanceButton.addEventListener('click', async (event) => {
                event.stopPropagation();
                enhanceButton.disabled = true; enhanceButton.textContent = "Enhancing..."; enhanceButton.style.opacity = "0.6"; enhanceButton.style.cursor = "not-allowed";
                await handleEnhanceClick(result);
                enhanceButton.disabled = false; enhanceButton.textContent = "✦ AI Enhance"; enhanceButton.style.opacity = "1"; enhanceButton.style.cursor = "pointer"; enhanceButton.style.backgroundColor = "#ffffff";
            });
        }

        // Left group order: Edit | Preview | LP Preview | AI Enhance (dev only)
        leftBtnGroup.appendChild(editButton);
        leftBtnGroup.appendChild(previewButton);
        leftBtnGroup.appendChild(lpPreviewButton);
        if (enhanceButton) leftBtnGroup.appendChild(enhanceButton);

        // Right group order: Delete | Tag & Approve
        rightBtnGroup.appendChild(deleteButton);
        rightBtnGroup.appendChild(tagAndApproveButton);
        btnContainer.appendChild(leftBtnGroup);
        btnContainer.appendChild(rightBtnGroup);

        wrapperDiv.appendChild(infoIconBtn);
        wrapperDiv.appendChild(placeholder);
        wrapperDiv.appendChild(iframe);
        wrapperDiv.appendChild(btnContainer);

        return { wrapperDiv, iframe, placeholder };
    };

    // Build all section shells immediately, then lazy-load iframe HTML on scroll
    const renderSections = (sections) => {
        const container = document.getElementById("new_existing_div_preview");
        if (!container || !isMountedRef.current) return;

        container.innerHTML = '';

        if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                const wrapper = entry.target;
                if (wrapper.dataset.loaded) return;
                wrapper.dataset.loaded = 'true';
                observer.unobserve(wrapper);
                const sectionData = wrapper._sectionData;
                if (!sectionData) return;
                const sid = sectionData.section_id.replace(/[^a-zA-Z0-9_-]/g, '_');
                const iframe = document.getElementById(sid);
                const placeholder = document.getElementById(`placeholder_${sid}`);
                if (iframe && placeholder) loadIframeContent(sectionData, iframe, placeholder);
            });
        }, { rootMargin: '300px', threshold: 0 });

        observerRef.current = observer;

        sections.forEach(result => {
            if (!isMountedRef.current) return;
            const sanitizedId = result.section_id.replace(/[^a-zA-Z0-9_-]/g, '_');
            if (document.getElementById(`wrapper_${sanitizedId}`)) return;

            const { wrapperDiv, iframe, placeholder } = buildSectionShell(result);
            wrapperDiv._sectionData = result;
            container.appendChild(wrapperDiv);

            if (htmlCacheRef.current[result.section_id]) {
                // Already cached from a previous load — inject immediately
                wrapperDiv.dataset.loaded = 'true';
                loadIframeContent(result, iframe, placeholder);
            } else {
                observer.observe(wrapperDiv);
            }
        });
    };

    // ── Effects ─────────────────────────────────────────────────────────────────

    // Mount / unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
        };
    }, []);

    // CDN stylesheet + script
    useEffect(() => {
        try {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = `https://scalex-ai-existing-div-cdn.web.app/${shop_domain.replace('.myshopify.com', '')}/style.css`;
            document.head.insertBefore(link, document.head.firstChild);

            const script = document.createElement("script");
            script.src = `https://scalex-ai-existing-div-cdn.web.app/${shop_domain.replace('.myshopify.com', '')}/existing_div.js`;
            script.async = true;
            document.head.insertBefore(script, link.nextSibling);

            return () => {
                if (link.parentNode) link.parentNode.removeChild(link);
                if (script.parentNode) script.parentNode.removeChild(script);
            };
        } catch (error) {
            console.error("Error:", error);
            toast.error(`${errorMessages["DATA_LOAD_ERROR"]["title"]}`);
        }
    }, [shop_domain]);

    // Landing pages + section tags
    useEffect(() => {
        fetchLandingPages();
        fetchExperimentLandingPages();
        if (app_url) {
            axios.get(`${app_url}/api/settings/sections-tags`, { withCredentials: true })
                .then(res => { if (res.data.success) setSectionsTags(res.data.data || []); })
                .catch(err => console.error('Error loading sections tags:', err));
        }
    }, [app_url]);

    // Synchronous DOM wipe before paint when client switches — prevents old sections flashing
    useLayoutEffect(() => {
        const container = document.getElementById('new_existing_div_preview');
        if (container) container.innerHTML = '';
        if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
        htmlCacheRef.current = {};
    }, [shop_domain]);

    // Initial section list load — spinner clears once list arrives, not after HTML loads
    useEffect(() => {
        if (!Cookies.get('shop')) return;
        // Reset stale data from the previous client immediately
        setAllSections([]);
        setFilteredSections([]);
        setIsInitialLoad(true);
        setLoading(true);

        // Bust CDN cache
        const timestamp = Date.now();
        const cdnLink   = document.querySelector(`link[href*="scalex-ai-existing-div-cdn.web.app"]`);
        const cdnScript = document.querySelector(`script[src*="scalex-ai-existing-div-cdn.web.app"]`);
        const slug = shop_domain.replace('.myshopify.com', '');
        if (cdnLink)   cdnLink.href   = `https://scalex-ai-existing-div-cdn.web.app/${slug}/style.css?t=${timestamp}`;
        if (cdnScript) cdnScript.src  = `https://scalex-ai-existing-div-cdn.web.app/${slug}/existing_div.js?t=${timestamp}`;

        axios.post(app_url + "/api/firebase/get_sections", { shop_domain: Cookies.get('shop') }, { withCredentials: true })
            .then(res => {
                const sections = res.data["data"] || [];
                setAllSections(sections);
                const filtered = filterSectionsByType(sections, selectedSectionType);
                setFilteredSections(filtered);
                setLoading(false);
                setIsInitialLoad(false);
                renderSections(filtered);
            })
            .catch(err => {
                setLoading(false);
                toast.error('Section fetch failed');
                console.error(err);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app_url, shop_domain]);

    // Filter change — cached sections appear instantly, new ones lazy-load
    useEffect(() => {
        if (allSections.length > 0 && !isInitialLoad) {
            const filtered = filterSectionsByType(allSections, selectedSectionType);
            setFilteredSections(filtered);
            renderSections(filtered);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSectionType, allSections, isInitialLoad]);

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <div className='main-container' id='all_sections_main_container'>
            {!shop_domain ? (
                <div className='go_to_shopify_admin_div'>
                    <h5>!! Go to Shopify Admin and open our app !!</h5>
                </div>
            ) : (
                <>
                    <div
                        className='main-title mt-4 mx-4 d-flex justify-content-between align-items-center'
                        style={{ marginBottom: '10px', border: '1px solid #e9ecef', borderRadius: '8px', padding: '16px 20px', backgroundColor: '#ffffff' }}
                    >
                        <h2 style={{ margin: 0, fontWeight: 'bold' }}>All Sections</h2>
                        <div className='d-flex gap-2 align-items-center'>
                            <button
                                onClick={() => window.location.reload()}
                                disabled={loading}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #dee2e6', borderRadius: '6px', padding: '6px 12px', backgroundColor: 'transparent', color: '#212529', cursor: loading ? 'not-allowed' : 'pointer' }}
                            >
                                {loading
                                    ? <Spinner size="sm" />
                                    : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}><path d="M12 20C9.76667 20 7.875 19.225 6.325 17.675C4.775 16.125 4 14.2333 4 12C4 9.76667 4.775 7.875 6.325 6.325C7.875 4.775 9.76667 4 12 4C13.15 4 14.25 4.23734 15.3 4.712C16.35 5.18667 17.25 5.866 18 6.75V5C18 4.71667 18.096 4.47934 18.288 4.288C18.48 4.09667 18.7173 4.00067 19 4C19.2827 3.99934 19.5203 4.09534 19.713 4.288C19.9057 4.48067 20.0013 4.718 20 5V10C20 10.2833 19.904 10.521 19.712 10.713C19.52 10.905 19.2827 11.0007 19 11H14C13.7167 11 13.4793 10.904 13.288 10.712C13.0967 10.52 13.0007 10.2827 13 10C12.9993 9.71734 13.0953 9.48 13.288 9.288C13.4807 9.096 13.718 9 14 9H17.2C16.6667 8.06667 15.9377 7.33334 15.013 6.8C14.0883 6.26667 13.084 6 12 6C10.3333 6 8.91667 6.58334 7.75 7.75C6.58333 8.91667 6 10.3333 6 12C6 13.6667 6.58333 15.0833 7.75 16.25C8.91667 17.4167 10.3333 18 12 18C13.1333 18 14.171 17.7127 15.113 17.138C16.055 16.5633 16.784 15.7923 17.3 14.825C17.4333 14.5917 17.621 14.4293 17.863 14.338C18.105 14.2467 18.3507 14.2423 18.6 14.325C18.8667 14.4083 19.0583 14.5833 19.175 14.85C19.2917 15.1167 19.2833 15.3667 19.15 15.6C18.4667 16.9333 17.4917 18 16.225 18.8C14.9583 19.6 13.55 20 12 20Z" fill="#535353"/></svg>
                                }
                                Refresh
                            </button>
                            <select
                                className='form-select form-select-sm'
                                value={selectedSectionType}
                                onChange={(e) => setSelectedSectionType(e.target.value)}
                                style={{ minWidth: '150px', border: '1px solid #dee2e6', borderRadius: '6px', padding: '6px 12px', fontSize: '14px' }}
                            >
                                <option value="all">All Types</option>
                                <option value="assets">Assets</option>
                                <option value="extension">Extension</option>
                                <option value="custom">Custom</option>
                            </select>
                        </div>
                    </div>
                    <div id='existing_div_preview' className='px-2'></div>
                </>
            )}

            {!loading ? (
                <div id='new_existing_div_preview' className='mx-3 p-2'></div>
            ) : (
                <div className='main_conatiner_spinner'>
                    <Spinner animation='border' />
                </div>
            )}

            {/* Tag & Approve (combined) Modal */}
            <Modal show={showTagAndApproveModal} onHide={() => { if (!tagAndApproveBusy) setShowTagAndApproveModal(false); }} centered size="lg">
                <Modal.Header
                    closeButton={!tagAndApproveBusy}
                    closeVariant="white"
                    style={{ backgroundColor: '#132848', color: '#ffffff', borderBottom: 'none', padding: '16px 24px' }}
                >
                    <Modal.Title style={{ color: '#ffffff', fontWeight: '600', fontSize: '18px' }}>Approve Section</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <Form>
                        {/* Single Landing Page — used as BOTH the tag target and the approve target. */}
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#132848', fontWeight: '600', fontSize: '14px' }}>
                                Landing Page <span style={{ color: '#dc3545' }}>*</span>
                            </Form.Label>
                            <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 6px 0' }}>
                                The section will be tagged to this landing page and approved on it.
                            </p>
                            <Form.Select
                                value={selectedLandingPage}
                                onChange={(e) => setSelectedLandingPage(e.target.value)}
                                required
                                disabled={tagAndApproveBusy}
                                style={{ borderColor: '#dee2e6', borderRadius: '6px', fontSize: '14px' }}
                            >
                                <option value="">Choose a landing page...</option>
                                {landingPages.map((lp, i) => <option key={i} value={lp}>{lp}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#132848', fontWeight: '600', fontSize: '14px' }}>Section Category</Form.Label>
                            <Form.Select
                                value={selectedCategory}
                                onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubCategory(''); }}
                                disabled={tagAndApproveBusy}
                                style={{ borderColor: '#dee2e6', borderRadius: '6px', fontSize: '14px' }}
                            >
                                <option value="">Select Category</option>
                                {sectionsTags.map(tag => (
                                    <option key={tag.id} value={tag.category}>{tag.category}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#132848', fontWeight: '600', fontSize: '14px' }}>Section Sub Category</Form.Label>
                            <Form.Select
                                value={selectedSubCategory}
                                onChange={(e) => setSelectedSubCategory(e.target.value)}
                                disabled={!selectedCategory || tagAndApproveBusy}
                                style={{ borderColor: '#dee2e6', borderRadius: '6px', fontSize: '14px' }}
                            >
                                <option value="">Select Sub Category</option>
                                {(sectionsTags.find(tag => tag.category === selectedCategory)?.subcategories || []).map((sc, i) => (
                                    <option key={i} value={sc}>{sc}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={handleTagAndApproveSubmit}
                        disabled={!selectedLandingPage || tagAndApproveBusy}
                        style={{
                            padding: '8px 20px',
                            fontWeight: '500',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#ffffff',
                            backgroundColor: !selectedLandingPage || tagAndApproveBusy ? '#6c8aaa' : '#132848',
                            border: 'none',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { if (selectedLandingPage && !tagAndApproveBusy) e.currentTarget.style.backgroundColor = '#0f1f35'; }}
                        onMouseLeave={(e) => { if (selectedLandingPage && !tagAndApproveBusy) e.currentTarget.style.backgroundColor = '#132848'; }}
                    >
                        {tagAndApproveBusy ? <><Spinner size="sm" className="me-1" />Working...</> : 'Approve'}
                    </Button>
                    <Button
                        onClick={() => setShowTagAndApproveModal(false)}
                        disabled={tagAndApproveBusy}
                        style={{
                            padding: '8px 20px',
                            fontWeight: '500',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#495057',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dee2e6',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { if (!tagAndApproveBusy) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { if (!tagAndApproveBusy) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                    >
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Section Info Modal (read-only) */}
            <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered>
                <Modal.Header closeButton closeVariant="white"
                    style={{ backgroundColor: '#132848', color: '#ffffff', borderBottom: 'none', padding: '16px 24px' }}>
                    <Modal.Title style={{ color: '#ffffff', fontWeight: '600', fontSize: '18px' }}>Section Info</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <div className="mb-3">
                        <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '2px' }}>Section Name</div>
                        <div style={{ fontWeight: '600' }}>{infoSection?.section_name || infoSection?.section_id || '—'}</div>
                    </div>
                    <div className="mb-3">
                        <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '2px' }}>Live Version</div>
                        <span style={{ background: '#198754', color: '#fff', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' }}>
                            v{infoSection?.live_version || 1}
                        </span>
                    </div>
                    <div className="mb-3">
                        <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '2px' }}>Source URL</div>
                        {infoSection?.section_original_url
                            ? <a href={infoSection.section_original_url} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '13px', wordBreak: 'break-all' }}>{infoSection.section_original_url}</a>
                            : <span style={{ color: '#adb5bd' }}>—</span>
                        }
                    </div>
                    <div className="mb-3">
                        <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '4px' }}>Tagged Landing Pages</div>
                        {(sectionTagsMap[infoSection?.section_id] ?? infoSection?.tagged_landing_pages ?? []).length > 0
                            ? <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '13px' }}>
                                {(sectionTagsMap[infoSection?.section_id] ?? infoSection?.tagged_landing_pages ?? []).map((lp, i) =>
                                    <li key={i}>{lp}</li>
                                )}
                              </ul>
                            : <span style={{ fontSize: '13px', color: '#adb5bd' }}>None — all landing pages available</span>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px' }}>
                    <Button onClick={() => setShowInfoModal(false)}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#495057', backgroundColor: '#ffffff', border: '1px solid #dee2e6', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* LP Preview — pick tagged LP Modal */}
            <Modal show={showLpPickerModal} onHide={() => { if (!lpPickerBusy) setShowLpPickerModal(false); }} centered>
                <Modal.Header
                    closeButton={!lpPickerBusy}
                    closeVariant="white"
                    style={{ backgroundColor: '#132848', color: '#ffffff', borderBottom: 'none', padding: '16px 24px' }}
                >
                    <Modal.Title style={{ color: '#ffffff', fontWeight: '600', fontSize: '18px' }}>Preview Section on Landing Page</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
                        Pick a landing page from the ones this section is tagged to. The preview will inject only this section into that LP.
                    </p>
                    <Form.Group>
                        <Form.Label style={{ color: '#132848', fontWeight: '600', fontSize: '14px' }}>Landing Page</Form.Label>
                        <Form.Select
                            value={lpPickerSelected}
                            onChange={(e) => setLpPickerSelected(e.target.value)}
                            disabled={lpPickerBusy}
                            style={{ borderColor: '#dee2e6', borderRadius: '6px', fontSize: '14px' }}
                        >
                            {lpPickerOptions.map((lp, i) => <option key={i} value={lp}>{lp}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={() => setShowLpPickerModal(false)}
                        disabled={lpPickerBusy}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#495057', backgroundColor: '#ffffff', border: '1px solid #dee2e6', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { if (!lpPickerBusy) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { if (!lpPickerBusy) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submitLpPreview}
                        disabled={!lpPickerSelected || lpPickerBusy}
                        style={{
                            padding: '8px 20px',
                            fontWeight: '500',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#ffffff',
                            backgroundColor: !lpPickerSelected || lpPickerBusy ? '#6c8aaa' : '#132848',
                            border: 'none',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { if (lpPickerSelected && !lpPickerBusy) e.currentTarget.style.backgroundColor = '#0f1f35'; }}
                        onMouseLeave={(e) => { if (lpPickerSelected && !lpPickerBusy) e.currentTarget.style.backgroundColor = '#132848'; }}
                    >
                        {lpPickerBusy ? <><Spinner size="sm" className="me-1" />Generating...</> : 'Generate Preview'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => { if (!isDeleting) setShowDeleteModal(false); }} centered>
                <Modal.Header
                    closeButton={!isDeleting}
                    closeVariant="white"
                    style={{ backgroundColor: '#132848', color: '#ffffff', borderBottom: 'none', padding: '16px 24px' }}
                >
                    <Modal.Title style={{ color: '#ffffff', fontWeight: '600', fontSize: '18px' }}>Delete Section</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <p>Are you sure you want to delete <strong>{pendingDeleteName}</strong>?</p>
                    <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                        This will permanently remove the section and all its versions. This action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isDeleting}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#495057', backgroundColor: '#ffffff', border: '1px solid #dee2e6', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={isDeleting}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#ffffff', backgroundColor: isDeleting ? '#6c8aaa' : '#dc3545', border: 'none', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { if (!isDeleting) e.currentTarget.style.backgroundColor = '#bb2d3b'; }}
                        onMouseLeave={(e) => { if (!isDeleting) e.currentTarget.style.backgroundColor = '#dc3545'; }}
                    >
                        {isDeleting ? <><Spinner size="sm" className="me-1" />Deleting...</> : 'Delete Section'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ExistingDivs;
